import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, CheckCircle2, AlertCircle, SkipForward, Loader2, Send, ExternalLink, Puzzle } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import {
  listQbConnections,
  bulkPublishReceipts,
  publishCheckReceipt,
} from '../../lib/api';

/**
 * Phase 25.3 — Bulk Publish to QuickBooks modal.
 *
 * Flow:
 *   1. Pick a connection (if 2+ companies)
 *   2. Optional "Preview validation" (parallel publish-check on first 20)
 *   3. Confirm → POST /bulk-publish (serial, continue-on-error)
 *   4. Progress state while in-flight
 *   5. Final summary: Succeeded / Failed / Skipped lists
 */
export default function BulkPublishModal({ open, onClose, receiptIds, onDone }) {
  const { colors } = useTheme();
  const [connections, setConnections] = useState([]);
  const [loadingConns, setLoadingConns] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // array of {rid, ok, issues, transaction_type}
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setResult(null);
    (async () => {
      try {
        const data = await listQbConnections();
        setConnections(data.connections || []);
        if ((data.connections || []).length) setSelectedId(data.connections[0].id);
      } finally { setLoadingConns(false); }
    })();
  }, [open]);

  const selectedConn = useMemo(
    () => connections.find((c) => c.id === selectedId) || null,
    [connections, selectedId],
  );

  const runPreview = useCallback(async () => {
    if (!selectedConn) return;
    setPreviewing(true);
    const sample = receiptIds.slice(0, 20);
    try {
      const rows = await Promise.all(sample.map(async (rid) => {
        try {
          const r = await publishCheckReceipt(selectedConn.id, rid);
          return { rid, ok: r.ok, issues: r.issues || [], transaction_type: r.transaction_type };
        } catch (e) {
          return { rid, ok: false, issues: [{ code: 'network', message: e.detail || 'network error' }] };
        }
      }));
      setPreview(rows);
    } finally {
      setPreviewing(false);
    }
  }, [selectedConn, receiptIds]);

  const runPublish = async () => {
    if (!selectedConn) return;
    setPublishing(true);
    try {
      const res = await bulkPublishReceipts(selectedConn.id, { receipt_ids: receiptIds });
      setResult(res);
      onDone?.(res);
    } catch (e) {
      setResult({ total: receiptIds.length, succeeded: 0, failed: receiptIds.length, skipped_not_found: 0, items: [{ receipt_id: 'batch', status: 'failed', error: e.detail || 'network error' }] });
    } finally {
      setPublishing(false);
    }
  };

  const retryFailed = async () => {
    const failedIds = (result?.items || []).filter((i) => i.status === 'failed').map((i) => i.receipt_id);
    if (!failedIds.length) return;
    setPublishing(true);
    try {
      const res = await bulkPublishReceipts(selectedConn.id, { receipt_ids: failedIds });
      setResult(res);
      onDone?.(res);
    } finally {
      setPublishing(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="bulk-publish-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,12,16,0.56)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 640, maxHeight: '85vh',
          background: colors.surface, color: colors.text,
          border: `1px solid ${colors.border}`, borderRadius: 16,
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#2CA01C' }}>
              QuickBooks publishing
            </div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Publish {receiptIds.length} receipt{receiptIds.length === 1 ? '' : 's'}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="bulk-publish-close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 6 }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
          {loadingConns && (
            <div style={{ color: colors.textMuted, fontSize: 13 }}>Loading connections…</div>
          )}

          {!loadingConns && connections.length === 0 && (
            <div style={{ padding: 20, borderRadius: 10, background: colors.bg, border: `1px dashed ${colors.border}`, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Puzzle size={22} color={colors.textMuted} />
              <div>
                <div style={{ fontWeight: 700 }}>No QuickBooks company connected</div>
                <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 2 }}>
                  <Link to="/app/settings#integrations" style={{ color: colors.accent, fontWeight: 600 }}>
                    Connect a company in Settings → Integrations
                  </Link>
                </div>
              </div>
            </div>
          )}

          {!loadingConns && connections.length > 0 && !result && (
            <>
              {connections.length > 1 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11.5, color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Target company
                  </label>
                  <select
                    data-testid="bulk-conn-select"
                    value={selectedId || ''}
                    onChange={(e) => { setSelectedId(e.target.value); setPreview(null); }}
                    style={{
                      marginTop: 6, width: '100%',
                      padding: '8px 10px', borderRadius: 8,
                      border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text,
                      fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div
                style={{
                  padding: 14, borderRadius: 10,
                  background: 'linear-gradient(135deg, rgba(44,160,28,0.08) 0%, rgba(0,0,0,0) 100%)',
                  border: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                  You&apos;re about to publish <strong>{receiptIds.length}</strong> receipt
                  {receiptIds.length === 1 ? '' : 's'} to{' '}
                  <strong>{selectedConn?.company_name}</strong>. Already-published receipts will be
                  skipped. Validation failures will be shown per-row — you can retry them from the
                  summary.
                </div>
              </div>

              {!preview && (
                <button
                  type="button"
                  data-testid="bulk-publish-preview-btn"
                  onClick={runPreview}
                  disabled={previewing || !selectedConn}
                  style={{
                    marginTop: 12,
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '7px 12px', borderRadius: 8,
                    background: 'transparent', color: colors.textMuted,
                    fontSize: 12, fontWeight: 700,
                    border: `1px solid ${colors.border}`, cursor: 'pointer',
                  }}
                >
                  {previewing ? <Loader2 size={13} className="animate-spin" /> : null}
                  {previewing ? 'Checking first 20…' : 'Preview validation (first 20)'}
                </button>
              )}

              {preview && (
                <div
                  data-testid="bulk-publish-preview"
                  style={{
                    marginTop: 12, padding: 10, borderRadius: 10,
                    background: colors.bg, border: `1px solid ${colors.border}`,
                    maxHeight: 220, overflow: 'auto',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, marginBottom: 6 }}>
                    Dry-run of first {preview.length} receipts
                  </div>
                  {preview.map((p) => (
                    <div key={p.rid} style={{ display: 'flex', gap: 7, alignItems: 'start', padding: '3px 0', fontSize: 12 }}>
                      {p.ok ? <CheckCircle2 size={12} color="#117A3C" /> : <AlertCircle size={12} color="#B42318" />}
                      <span style={{ color: colors.textMuted, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {p.rid.slice(0, 8)}
                      </span>
                      <span style={{ color: colors.text, flex: 1 }}>
                        {p.ok ? `Ready (${p.transaction_type || 'Purchase'})` : (p.issues[0]?.message || 'Validation failed')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {result && (
            <div data-testid="bulk-publish-result">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                <SummaryTile count={result.succeeded} label="Succeeded" color="#117A3C" bg="#E7F7EE" />
                <SummaryTile count={result.failed} label="Failed" color="#B42318" bg="#FDECEC" />
                <SummaryTile count={result.skipped_not_found} label="Skipped" color="#8A5A00" bg="#FFF4D6" />
              </div>

              {result.items.filter((i) => i.status === 'succeeded').length > 0 && (
                <RowGroup
                  title="Succeeded"
                  Icon={CheckCircle2}
                  tint="#117A3C"
                  bg="#E7F7EE"
                  colors={colors}
                  items={result.items.filter((i) => i.status === 'succeeded')}
                  render={(i) => (
                    <>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: colors.textMuted }}>{i.receipt_id.slice(0, 8)}</span>
                      <span>→ QB#{i.qb_transaction_id} <em style={{ color: colors.textMuted, fontSize: 11 }}>({i.transaction_type})</em></span>
                      <Link to={`/app/receipts/${i.receipt_id}`} style={{ marginLeft: 'auto', fontSize: 11, color: '#117A3C', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', gap: 3, alignItems: 'center' }}>
                        View <ExternalLink size={10} />
                      </Link>
                    </>
                  )}
                />
              )}

              {result.items.filter((i) => i.status === 'failed').length > 0 && (
                <RowGroup
                  title="Failed"
                  Icon={AlertCircle}
                  tint="#B42318"
                  bg="#FDECEC"
                  colors={colors}
                  items={result.items.filter((i) => i.status === 'failed')}
                  render={(i) => (
                    <>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: colors.textMuted }}>{i.receipt_id.slice(0, 8)}</span>
                      <span style={{ color: '#8C1E12' }}>{i.error || 'Error'}</span>
                      <Link to={`/app/receipts/${i.receipt_id}`} style={{ marginLeft: 'auto', fontSize: 11, color: '#B42318', fontWeight: 700, textDecoration: 'none' }}>Fix →</Link>
                    </>
                  )}
                />
              )}

              {result.items.filter((i) => i.status === 'skipped').length > 0 && (
                <RowGroup
                  title="Skipped (already published)"
                  Icon={SkipForward}
                  tint="#8A5A00"
                  bg="#FFF4D6"
                  colors={colors}
                  items={result.items.filter((i) => i.status === 'skipped')}
                  render={(i) => (
                    <>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: colors.textMuted }}>{i.receipt_id.slice(0, 8)}</span>
                      <span>already published as QB#{i.qb_transaction_id}</span>
                    </>
                  )}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: 'transparent', color: colors.textMuted,
              fontSize: 13, fontWeight: 600,
              border: `1px solid ${colors.border}`, cursor: 'pointer',
            }}
          >
            Close
          </button>

          {!result && connections.length > 0 && (
            <button
              type="button"
              data-testid="bulk-publish-confirm"
              onClick={runPublish}
              disabled={publishing || !selectedConn}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '9px 16px', borderRadius: 9,
                background: '#2CA01C', color: 'white',
                fontSize: 13, fontWeight: 700,
                border: 'none', cursor: publishing ? 'wait' : 'pointer',
              }}
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {publishing ? 'Publishing…' : `Publish ${receiptIds.length} receipt${receiptIds.length === 1 ? '' : 's'}`}
            </button>
          )}

          {result && result.failed > 0 && (
            <button
              type="button"
              data-testid="bulk-publish-retry-failed"
              onClick={retryFailed}
              disabled={publishing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '9px 14px', borderRadius: 9,
                background: colors.accent, color: 'white',
                fontSize: 13, fontWeight: 700,
                border: 'none', cursor: publishing ? 'wait' : 'pointer',
              }}
            >
              Retry {result.failed} failed
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ count, label, color, bg }) {
  return (
    <div
      data-testid={`bulk-summary-${label.toLowerCase().split(' ')[0]}`}
      style={{
        padding: 14, borderRadius: 10, background: bg,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{count}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
}

function RowGroup({ title, Icon, tint, bg, colors, items, render }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{ marginBottom: 12, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', textAlign: 'left',
          padding: '9px 12px', background: bg, border: 'none',
          color: tint, fontSize: 12, fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
        }}
      >
        <Icon size={13} />
        {title} ({items.length})
      </button>
      {open && (
        <div>
          {items.map((i) => (
            <div
              key={i.receipt_id + (i.qb_transaction_id || '')}
              style={{ padding: '7px 12px', fontSize: 12, color: colors.text, display: 'flex', gap: 8, alignItems: 'center', borderTop: `1px solid ${colors.border}` }}
            >
              {render(i)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
